// ✅ src/pages/VehicleV2/LubeConsumptionReport.jsx
// Production listing template
// GET /api/lubeConsumptionReport/init
// GET /api/lubeConsumptionReport/list

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaArrowLeft,
  FaFileExport,
  FaEye,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com";

const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

const ID_COL_RE = /(^id$|_id$|typeid|categoryid|branchid)/i;
const IMPORTANT_COL_RE = /name|reg|code|vehicle|item|total|qty|branch|store|supplier/i;
const DAY_COL_RE = /^(d)?([1-9]|[12][0-9]|3[01])$/i;

const LubeConsumptionReport = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const [showFilters, setShowFilters] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [metaLoading, setMetaLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);
  const [itemCategory, setItemCategory] = useState({ id: "37", name: "LUBRICANTS", locked: true });
  const [itemNames, setItemNames] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [userLock, setUserLock] = useState({ branchLocked: false, branchValue: "" });

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);

  const [filters, setFilters] = useState({
    branch: "Select",
    month: "Select",
    year: "Select",
    itemCategoryId: "37",
    itemId: "Select",
  });

  const branchType = toStr(localStorage.getItem("branchtype") || localStorage.getItem("branchType") || "HO");
  const loginBranchId = toStr(localStorage.getItem("branchid") || localStorage.getItem("branchId") || "");
  const isBusy = metaLoading || loading;

  const fieldClass = `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const displayColumns = useMemo(() => {
    const all = columns.length ? columns : rows[0] ? Object.keys(rows[0]) : [];
    if (!all.length) return [];
    const kept = all.filter((c) => IMPORTANT_COL_RE.test(c) || DAY_COL_RE.test(String(c)) || /total/i.test(c));
    const cleaned = kept.filter((c) => !ID_COL_RE.test(String(c)));
    return cleaned.length ? cleaned : all;
  }, [columns, rows]);

  const currentItems = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return rows.slice(indexOfFirstItem, indexOfLastItem);
  }, [rows, currentPage]);

  const totalPages = Math.ceil(rows.length / itemsPerPage) || 1;

  const handleChange = (e) => setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));

  const toggleSupplier = (label) => {
    setSelectedSuppliers((prev) => (prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]));
  };

  const loadInit = async () => {
    try {
      setMetaLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/lubeConsumptionReport/init`, {
        params: { branchType, branchId: loginBranchId || undefined },
      });
      if (!data?.success) throw new Error(data?.message || "Init failed");
      setBranches(data.branches || []);
      setMonths(data.months || []);
      setYears(data.years || []);
      setItemCategory(data.itemCategory || { id: "37", name: "LUBRICANTS", locked: true });
      setItemNames(data.itemNames || []);
      setSuppliers(data.suppliers || []);
      setUserLock(data.userLock || { branchLocked: false, branchValue: "" });
      setFilters((p) => ({
        ...p,
        branch: data.userLock?.branchLocked ? data.userLock.branchValue || "Select" : p.branch || "Select",
        itemCategoryId: data.itemCategory?.id || "37",
      }));
    } catch (err) {
      console.error("init error:", err);
      toast.error(err?.response?.data?.message || err.message || "Failed to load init data");
    } finally {
      setMetaLoading(false);
    }
  };

  useEffect(() => {
    loadInit();
  }, []);

  const fetchList = async (override = {}) => {
    const f = { ...filters, ...override };
    if (isSelect(f.month)) return toast.error("Please select Month");
    if (isSelect(f.year)) return toast.error("Please select Year");
    if (isSelect(f.branch)) return toast.error("Please select Branch");

    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/lubeConsumptionReport/list`, {
        params: {
          branchId: f.branch,
          month: f.month,
          year: f.year,
          itemCategoryId: f.itemCategoryId || "37",
          itemId: f.itemId || "Select",
          suppliers: selectedSuppliers,
          date: "",
        },
      });
      if (!data?.success) throw new Error(data?.message || "List failed");
      setRows(data.rows || []);
      setColumns(data.columns || []);
      setTotal(data.total || 0);
      setCurrentPage(1);
      toast.success(`Found ${data.total ?? (data.rows || []).length} records`);
    } catch (err) {
      console.error("list error:", err);
      toast.error(err?.response?.data?.message || err.message || "Failed to fetch list");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchList();
  const handleViewAll = () => {
    setFilters((p) => ({ ...p, itemId: "Select" }));
    setSelectedSuppliers([]);
    fetchList({ itemId: "Select" });
  };
  const handleClearFilters = () => {
    setFilters((p) => ({
      ...p,
      month: "Select",
      year: "Select",
      itemId: "Select",
      branch: userLock?.branchLocked ? userLock.branchValue || "Select" : "Select",
      itemCategoryId: itemCategory?.id || "37",
    }));
    setSelectedSuppliers([]);
    setRows([]);
    setColumns([]);
    setTotal(0);
    setCurrentPage(1);
    toast.info("Filters cleared!");
  };

  const handlePrint = () => {
    const qs = new URLSearchParams({
      branchId: filters.branch,
      month: filters.month,
      year: filters.year,
      itemCategoryId: filters.itemCategoryId || "37",
      itemId: filters.itemId || "Select",
    }).toString();
    window.open(`/vehicle/LubeConsumptionReport_Print?${qs}`, "_blank");
  };

  const handleExport = () => toast.success("Export: hook your CSV/XLSX export here");

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">LUBE CONSUMPTION REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white" : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>Lube Consumption Report</h2>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button onClick={handlePrint} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white`}><FaPrint /> Print</button>
              <button onClick={handleExport} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}><FaFileExport /> Export</button>
              <button onClick={loadInit} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSyncAlt /> Refresh</button>
              <button onClick={() => navigate(-1)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}><FaArrowLeft /> Back</button>
              <button onClick={() => setShowFilters((s) => !s)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>Search Criteria</h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleClearFilters} type="button" className={`px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"}`}>Clear</button>
                  <button onClick={handleViewAll} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaEye /> View All</button>
                  <button onClick={handleSearch} disabled={isBusy} type="button" className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSearch /> {loading ? "Searching..." : "Search"}</button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>Branch</label>
                  <select name="branch" value={filters.branch} onChange={handleChange} className={fieldClass} disabled={!!userLock?.branchLocked || isBusy}>
                    <option value="Select">Select</option>
                    {branches.map((b) => <option key={toStr(b.value)} value={toStr(b.value)}>{toStr(b.label)}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Month</label>
                  <select name="month" value={filters.month} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    {months.map((m) => <option key={toStr(m.value)} value={toStr(m.value)}>{toStr(m.label)}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Year</label>
                  <select name="year" value={filters.year} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    {years.map((y) => <option key={toStr(y.value)} value={toStr(y.value)}>{toStr(y.label)}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Item Category</label>
                  <input className={fieldClass} value={`${itemCategory?.name || "Item Category"} (${itemCategory?.id || "37"})`} disabled />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Item Name</label>
                  <select name="itemId" value={filters.itemId} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    {itemNames.map((it) => <option key={toStr(it.value)} value={toStr(it.value)}>{toStr(it.label)}</option>)}
                  </select>
                </div>
                <div className="min-w-0 flex items-end">
                  <div className={`text-sm pb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>Suppliers selected: <b>{selectedSuppliers.length}</b></div>
                </div>
              </div>

              <div className="mt-4">
                <label className={labelClass}>Suppliers</label>
                <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 p-3 rounded-lg border max-h-40 overflow-auto ${isDark ? "border-gray-700 bg-gray-900/20" : "border-gray-200 bg-white"}`}>
                  {suppliers.length === 0 ? (
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{metaLoading ? "Loading suppliers..." : "No suppliers"}</div>
                  ) : (
                    suppliers.map((s) => {
                      const label = toStr(s.label);
                      return (
                        <label key={label} className={`flex items-center gap-2 text-sm min-w-0 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                          <input type="checkbox" checked={selectedSuppliers.includes(label)} onChange={() => toggleSupplier(label)} />
                          <span className="truncate">{label}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
              Total Records: {rows.length}{typeof total === "number" && total !== rows.length ? ` / ${total}` : ""}
            </div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{metaLoading ? "Loading dropdowns..." : loading ? "Searching..." : "Ready"}</div>
          </div>

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {loading || metaLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}><div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div></div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</div>
                <p className={`mt-2 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>Select Month / Year / Branch and click Search.</p>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    {displayColumns.map((c) => <th key={c} className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">{c}</th>)}
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Print</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {currentItems.map((row, idx) => (
                    <tr key={idx} className={`${idx % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                      {displayColumns.map((c) => (
                        <td key={c} className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{toStr(row[c])}</td>
                      ))}
                      <td className="px-3 py-3">
                        <button onClick={handlePrint} type="button" className={`p-2 rounded ${isDark ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-100 text-gray-800 hover:bg-gray-200"}`} title="Print">
                          <FaPrint className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {rows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, rows.length)} of {rows.length} entries
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}`}>Prev</button>
                <div className={`px-4 py-2 border rounded-lg ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>Page {currentPage} / {totalPages}</div>
                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""}`}>Next</button>
              </div>
            </div>
          )}

          <p className={`text-xs text-right mt-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>INFRA SCREEN : B-60</p>
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default LubeConsumptionReport;